import { HttpEvent, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { HTTP_DISABLE_ACCESS_TOKEN } from '@app/core/http/http-context';
import { ApiUrlService } from '@app/shared/services/api-url.service';
import { OAuthResourceServerErrorHandler } from 'angular-oauth2-oidc';
import { merge, Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, take, timeout } from 'rxjs/operators';
import { IOAuthService } from '../ioauth-service';

/**
 * Matches a request against trusted API/CDN bases using parsed origins and
 * path boundaries. String prefix checks are intentionally avoided so a host
 * such as api.example.com.attacker.invalid cannot receive the access token.
 */
export function isTrustedOAuthResourceUrl(
  requestUrl: string,
  trustedBaseUrls: readonly string[],
): boolean {
  try {
    const request = new URL(requestUrl, window.location.origin);

    return trustedBaseUrls.some((baseUrl) => {
      const trusted = new URL(baseUrl, window.location.origin);
      if (request.origin !== trusted.origin) {
        return false;
      }

      const basePath = trusted.pathname.endsWith('/')
        ? trusted.pathname
        : `${trusted.pathname}/`;
      const exactBasePath = basePath === '/' ? '/' : basePath.slice(0, -1);
      return basePath === '/' || request.pathname === exactBasePath || request.pathname.startsWith(basePath);
    });
  } catch {
    return false;
  }
}

export function handleOAuthRequest(
  req: HttpRequest<any>,
  next: (request: HttpRequest<any>) => Observable<HttpEvent<any>>,
  oAuthService: IOAuthService,
  errorHandler: OAuthResourceServerErrorHandler,
  trustedBaseUrls: readonly string[],
): Observable<HttpEvent<any>> {
  if (!isTrustedOAuthResourceUrl(req.url, trustedBaseUrls)) {
    return next(req);
  }

  return merge(
    of(oAuthService.getAccessToken()).pipe(filter((token) => !!token)),
    oAuthService.events.pipe(
      filter((e) => e.type === 'token_received'),
      timeout(oAuthService.waitForTokenInMsec ?? 0),
      catchError(() => of(null)),
      map(() => oAuthService.getAccessToken())
    )
  ).pipe(
    take(1),
    mergeMap((token) => {
      const authorizedRequest = token
        ? req.clone({ headers: req.headers.set('Authorization', 'Bearer ' + token) })
        : req;
      return next(authorizedRequest).pipe(catchError((err) => errorHandler.handleError(err)));
    })
  );
}

export const defaultOAuthInterceptorFn: HttpInterceptorFn = (req, next) => {
  if (req.context.get(HTTP_DISABLE_ACCESS_TOKEN)) {
    return next(req);
  }

  const apiUrlService = inject(ApiUrlService);
  let trustedBaseUrls: readonly string[];
  try {
    trustedBaseUrls = [apiUrlService.getApiUrl(), apiUrlService.getCdnUrl()];
  } catch {
    // Tenant resolution is not ready. Fail closed instead of leaking a token
    // to an absolute URL whose ownership cannot be established yet.
    return next(req);
  }

  return handleOAuthRequest(
    req,
    next,
    inject(IOAuthService),
    inject(OAuthResourceServerErrorHandler),
    trustedBaseUrls,
  );
};
